"use client";

import Link from "next/link";

import { motion, type Variants } from "framer-motion";
import { CheckCircle2, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

type CTA = {
  label: string;
  href: string;
  variant?: "default" | "outline";
};

export type PricingCardProps = {
  title: string;
  price: string;
  frequency: string;
  badge: string;
  badgeVariant?: "default" | "outline" | "success";
  value: string;
  note?: string;
  features: string[];
  highlight?: boolean;
  hostingInfo?: string;
  ctaPrimary: CTA;
  ctaSecondary?: CTA;
  delay?: number;
};

export function PricingCard({
  title,
  price,
  frequency,
  badge,
  badgeVariant = "outline",
  value,
  note,
  features,
  highlight,
  hostingInfo = "Hosting & CDN termasuk",
  ctaPrimary,
  ctaSecondary,
  delay = 0,
}: PricingCardProps) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={cardVariants}
      transition={{
        duration: 0.35,
        ease: "easeOut",
        delay,
      }}
      whileHover={{ scale: 1.02, y: -8 }}
      className={cn(
        "relative rounded-[28px]",
        highlight &&
          "before:absolute before:inset-0 before:-z-10 before:rounded-[34px] before:bg-gradient-to-r before:from-[#35A29F]/30 before:via-[#088395]/25 before:to-[#071952]/30 before:blur-3xl",
      )}
    >
      <Card
        className={cn(
          "h-full rounded-[28px] border border-white/60 bg-white/95 backdrop-blur-sm transition-all",
          highlight
            ? "border-[#35A29F]/50 shadow-[0_25px_80px_-40px_rgba(8,131,149,.7)] lg:scale-[1.02]"
            : "border-[#071952]/10 shadow-[0_25px_70px_-50px_rgba(7,25,82,.25)]",
        )}
      >
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-between">
            <Badge variant={highlight ? "success" : badgeVariant}>{badge}</Badge>
            {highlight && (
              <div className="flex items-center gap-1 rounded-full bg-[#F2F7A1]/80 px-3 py-1 text-xs font-semibold text-[#071952]">
                <Sparkles className="h-3.5 w-3.5" />
                Best Pick
              </div>
            )}
          </div>
          <CardTitle className="text-2xl text-[#071952]">{title}</CardTitle>
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-semibold text-[#071952]">{price}</span>
              <span className="text-sm font-medium text-[#071952]/70">{frequency}</span>
            </div>
            <p className="text-xs tracking-[0.35em] text-[#088395]">
              {hostingInfo}
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-base font-medium text-[#071952]">{value}</p>
          <ul className="space-y-3 text-sm text-[#071952]/80">
            {features.map((feature) => (
              <li key={feature} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#35A29F]" />
                <span className="leading-relaxed">{feature}</span>
              </li>
            ))}
          </ul>

          {note && (
            <p className="rounded-2xl bg-[#F2F7A1]/70 px-4 py-3 text-sm font-medium text-[#071952]">
              {note}
            </p>
          )}

          <div className="flex flex-col gap-3 pt-2 sm:flex-row">
            <motion.div whileHover={{ y: -2 }} className="flex-1">
              <Button
                asChild
                className="w-full rounded-2xl py-5 text-base"
                variant={ctaPrimary.variant ?? "default"}
              >
                <Link href={ctaPrimary.href}>{ctaPrimary.label}</Link>
              </Button>
            </motion.div>
            {ctaSecondary && (
              <motion.div whileHover={{ y: -2 }} className="flex-1">
                <Button
                  asChild
                  variant={ctaSecondary.variant ?? "outline"}
                  className="w-full rounded-2xl py-5 text-base"
                >
                  <Link href={ctaSecondary.href}>{ctaSecondary.label}</Link>
                </Button>
              </motion.div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
